/**
 * Member Consent Panel
 * Displays and manages consent categories for an individual member.
 * Shows grouped toggles, bulk actions, and an expandable audit trail.
 * Consent-type form templates are surfaced as deep-links (TRUS-137).
 */

import React, { useState, useEffect } from 'react';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Clock,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
  MessageSquare,
  Share2,
  Loader2,
  RefreshCw,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  memberConsentAPI,
  ConsentCategory,
  MemberConsent,
  ConsentAuditEntry,
} from '@/services/api/memberConsent';
import { formsAPI, FormTemplate } from '@/services/api/forms';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface MemberConsentPanelProps {
  memberId: number;
}

const CONSENT_METHODS = [
  { value: 'verbal', label: 'Verbal Consent' },
  { value: 'written', label: 'Written Consent' },
  { value: 'digital_signature', label: 'Digital Signature' },
  { value: 'portal', label: 'Member Portal' },
  { value: 'checkbox', label: 'Checkbox / Form' },
];

const GROUP_CONFIG: Record<string, { label: string; icon: React.ElementType }> = {
  enrichment: { label: 'Data Enrichment Consent', icon: Shield },
  communication: { label: 'Communication Preferences', icon: MessageSquare },
  data_sharing: { label: 'Data Sharing Consent', icon: Share2 },
};

const GROUP_ORDER = ['enrichment', 'communication', 'data_sharing'];

export default function MemberConsentPanel({ memberId }: MemberConsentPanelProps) {
  const { fmt } = useFormattedDate();
  const [categories, setCategories] = useState<ConsentCategory[]>([]);
  const [consents, setConsents] = useState<MemberConsent[]>([]);
  const [auditTrail, setAuditTrail] = useState<ConsentAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [consentMethod, setConsentMethod] = useState('verbal');
  const [witnessNotes, setWitnessNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);

  const [auditExpanded, setAuditExpanded] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  const [withdrawDialog, setWithdrawDialog] = useState<string | null>(null);
  const [withdrawReason, setWithdrawReason] = useState('');

  // Consent-type form templates surfaced for staff navigation (TRUS-137)
  const [consentForms, setConsentForms] = useState<FormTemplate[]>([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cats, memberConsents, consentTemplates] = await Promise.all([
        memberConsentAPI.getCategories(),
        memberConsentAPI.getMemberConsents(memberId),
        formsAPI.listFormTemplates({ form_type: 'consent', is_active: true }).catch(() => [] as FormTemplate[]),
      ]);
      setCategories(cats.filter((c) => c.is_active));
      setConsents(memberConsents);
      setConsentForms(Array.isArray(consentTemplates) ? consentTemplates : []);
      setError(null);
    } catch (err: any) {
      if (err?.response?.status === 500) {
        setCategories([]);
        setConsents([]);
        setError(null);
      } else {
        setError('Failed to load consent data');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAuditTrail = async () => {
    setAuditLoading(true);
    try {
      const trail = await memberConsentAPI.getAuditTrail(memberId, 50);
      setAuditTrail(trail);
    } catch {
      // silent — audit trail is supplementary
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [memberId]);

  useEffect(() => {
    if (auditExpanded && auditTrail.length === 0) {
      loadAuditTrail();
    }
  }, [auditExpanded]);

  const getConsentForCategory = (categoryKey: string): MemberConsent | undefined => {
    return consents.find((c) => c.category_key === categoryKey);
  };

  const isGranted = (categoryKey: string): boolean => {
    const consent = getConsentForCategory(categoryKey);
    return consent?.consent_status === 'granted';
  };

  const handleToggle = async (categoryKey: string, currentlyGranted: boolean) => {
    if (currentlyGranted) {
      setWithdrawDialog(categoryKey);
      return;
    }

    setActionLoading(categoryKey);
    try {
      await memberConsentAPI.grantConsent(
        memberId,
        [categoryKey],
        consentMethod,
        witnessNotes || undefined,
      );
      setSuccess('Consent granted');
      setTimeout(() => setSuccess(null), 2000);
      await loadData();
    } catch {
      setError('Failed to grant consent');
      setTimeout(() => setError(null), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const handleWithdrawConfirm = async () => {
    if (!withdrawDialog) return;
    setActionLoading(withdrawDialog);
    try {
      await memberConsentAPI.withdrawConsent(
        memberId,
        [withdrawDialog],
        withdrawReason || undefined,
      );
      setWithdrawDialog(null);
      setWithdrawReason('');
      setSuccess('Consent withdrawn');
      setTimeout(() => setSuccess(null), 2000);
      await loadData();
    } catch {
      setError('Failed to withdraw consent');
      setTimeout(() => setError(null), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantAll = async () => {
    setActionLoading('grant_all');
    try {
      await memberConsentAPI.bulkConsent(
        memberId,
        true,
        consentMethod,
        witnessNotes || undefined,
      );
      setSuccess('All consents granted');
      setTimeout(() => setSuccess(null), 2000);
      await loadData();
    } catch {
      setError('Failed to grant all consents');
      setTimeout(() => setError(null), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const handleWithdrawAll = async () => {
    setActionLoading('withdraw_all');
    try {
      await memberConsentAPI.bulkConsent(memberId, false, consentMethod);
      setSuccess('All consents withdrawn');
      setTimeout(() => setSuccess(null), 2000);
      await loadData();
    } catch {
      setError('Failed to withdraw all consents');
      setTimeout(() => setError(null), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const groupedCategories = GROUP_ORDER.map((group) => ({
    group,
    ...GROUP_CONFIG[group],
    categories: categories
      .filter((c) => c.category_group === group)
      .sort((a, b) => a.sort_order - b.sort_order),
  })).filter((g) => g.categories.length > 0);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    return fmt.dateTime(dateStr);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-500">Loading consent data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-500" />
            Member Consent Management
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage data consent preferences for this member
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} className="gap-1">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert>
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription className="text-green-700 dark:text-green-400">{success}</AlertDescription>
        </Alert>
      )}

      {/* Consent Method & Bulk Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm font-medium">Consent Method</Label>
              <Select value={consentMethod} onValueChange={setConsentMethod}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONSENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNotes(!showNotes)}
                className="gap-1 text-gray-600"
              >
                {showNotes ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Witness Notes
              </Button>
              <Button
                size="sm"
                onClick={handleGrantAll}
                disabled={actionLoading !== null}
                className="gap-1 bg-v2accent hover:bg-v2accent/90"
              >
                {actionLoading === 'grant_all' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                Grant All
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleWithdrawAll}
                disabled={actionLoading !== null}
                className="gap-1"
              >
                {actionLoading === 'withdraw_all' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                Withdraw All
              </Button>
            </div>
          </div>
          {showNotes && (
            <div className="mt-4">
              <Label className="text-sm font-medium">Witness / Staff Notes</Label>
              <Textarea
                value={witnessNotes}
                onChange={(e) => setWitnessNotes(e.target.value)}
                placeholder="Optional notes about how consent was collected (e.g., witness name, context)"
                rows={2}
                className="mt-1"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consent Categories by Group */}
      {groupedCategories.map(({ group, label, icon: GroupIcon, categories: groupCats }) => (
        <Card key={group}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <GroupIcon className="h-5 w-5 text-blue-500" />
              {label}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 pt-0">
            {groupCats.map((category) => {
              const consent = getConsentForCategory(category.category_key);
              const granted = consent?.consent_status === 'granted';
              const withdrawn = consent?.consent_status === 'withdrawn';
              const pending = !consent || consent.consent_status === 'pending';
              const isLoading = actionLoading === category.category_key;

              return (
                <div
                  key={category.category_key}
                  className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 border border-transparent hover:border-gray-200 dark:hover:border-gray-700 transition-colors"
                >
                  {/* Left: Status Icon */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {granted && <ShieldCheck className="h-5 w-5 text-green-500 flex-shrink-0" />}
                    {withdrawn && <ShieldOff className="h-5 w-5 text-red-500 flex-shrink-0" />}
                    {pending && <Clock className="h-5 w-5 text-yellow-500 flex-shrink-0" />}

                    {/* Middle: Name, Description, Badges */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-gray-900 dark:text-white">
                          {category.display_name}
                        </span>
                        {category.legal_basis === 'legitimate_interest' ? (
                          <Badge
                            className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 text-xs cursor-help"
                            title="Legitimate Interest: Processing is permitted without explicit opt-in because it is necessary for service delivery or member care. The member can still opt out at any time."
                          >
                            Legitimate Interest
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 text-xs">
                            Explicit Consent Required
                          </Badge>
                        )}
                        {granted && (
                          <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                            Granted
                          </Badge>
                        )}
                        {withdrawn && (
                          <Badge variant="outline" className="text-xs text-red-600 border-red-300">
                            Withdrawn
                          </Badge>
                        )}
                        {pending && (
                          <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-300">
                            Pending
                          </Badge>
                        )}
                      </div>
                      {category.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                          {category.description}
                        </p>
                      )}
                      {consent?.granted_at && granted && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Granted {formatDate(consent.granted_at)}
                          {consent.collected_by_name ? ` by ${consent.collected_by_name}` : ''}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Right: Toggle */}
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                    ) : (
                      <Switch
                        checked={granted}
                        onCheckedChange={() => handleToggle(category.category_key, granted)}
                        disabled={actionLoading !== null}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {/* Empty State */}
      {categories.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Shield className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No consent categories are configured yet.</p>
          </CardContent>
        </Card>
      )}

      {/* Consent Documents — form templates of type 'consent' deep-linked for staff (TRUS-137) */}
      {consentForms.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Consent Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {consentForms.map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between py-3 px-3 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-blue-200 dark:hover:border-blue-800 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                      {template.name}
                    </p>
                    {template.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {template.description}
                      </p>
                    )}
                  </div>
                </div>
                <a
                  href={`/forms/templates/${template.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex-shrink-0 ml-4 whitespace-nowrap"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Form
                </a>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Audit Trail */}
      <Card>
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setAuditExpanded(!auditExpanded)}
        >
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-500" />
              Consent Audit Trail
            </span>
            {auditExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </CardTitle>
        </CardHeader>
        {auditExpanded && (
          <CardContent className="pt-0">
            {auditLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                <span className="ml-2 text-gray-500 text-sm">Loading audit trail...</span>
              </div>
            ) : auditTrail.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No audit entries yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Performed By</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditTrail.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(entry.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              entry.action.includes('grant')
                                ? 'text-green-600 border-green-300'
                                : entry.action.includes('withdraw')
                                ? 'text-red-600 border-red-300'
                                : 'text-gray-600 border-gray-300'
                            }
                          >
                            {entry.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{entry.category_key}</TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {entry.performed_by_name || '—'}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500 max-w-48 truncate">
                          {entry.reason || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Withdraw Confirmation Dialog */}
      <Dialog open={withdrawDialog !== null} onOpenChange={() => { setWithdrawDialog(null); setWithdrawReason(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Withdraw Consent?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-500">
              Withdrawing consent may affect the services and enrichment modules available to this member.
              This action will be recorded in the audit trail.
            </p>
            <div>
              <Label className="text-sm font-medium">Reason (optional)</Label>
              <Textarea
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                placeholder="Reason for withdrawal (e.g., member request, data policy change)"
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setWithdrawDialog(null); setWithdrawReason(''); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleWithdrawConfirm}
              disabled={actionLoading !== null}
              className="gap-1"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
              Withdraw Consent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { MemberConsentPanel };
